import { type HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { aadConfig, getAccessToken, useMsalAccessToken } from '@services/aad';
import type { Patch } from '@services/dto/common';
import type { ProjectContentCategory } from '@services/dto/content';
import type { Project } from '@services/dto/project';
import { type ProjectContentEx, projectFilesActions } from '@services/state';
import { useAppDispatch, useAppSelector } from '@services/state/store';
import React from 'react';

// How long a patch waits for the socket before it gives up and says so.
const PatchConnectTimeoutMs = 10_000;

export type UseProjectFilesHook = {
  reset: () => void;

  files: ProjectContentEx[];
  setFiles: (files?: ProjectContentEx[]) => void;

  category?: ProjectContentCategory | ProjectContentCategory[];
  setCategory: (category: ProjectContentCategory | ProjectContentCategory[]) => void;
  categoryFiles: ProjectContentEx[];

  loading: boolean;
};

export const useProjectFiles = (): UseProjectFilesHook => {
  const state = useAppSelector((state) => state.projectFiles);
  const dispatch = useAppDispatch();

  const accessToken = useMsalAccessToken();

  const setFiles = React.useCallback(
    (files?: ProjectContentEx[]) => {
      dispatch(projectFilesActions.setFiles({ files: files ?? [], accessToken: accessToken ?? '' }));
    },
    [dispatch, accessToken],
  );

  const setCategory = React.useCallback(
    (category: ProjectContentCategory | ProjectContentCategory[]) => {
      dispatch(projectFilesActions.setCategory({ category, accessToken }));
    },
    [dispatch, accessToken],
  );

  return {
    loading: state.loading,
    files: state.files,
    setFiles,
    category: state.category,
    setCategory,
    categoryFiles: state.categoryFiles,
    reset: () => dispatch(projectFilesActions.reset()),
  };
};

export type UseProjectFilesWsHook = UseProjectFilesHook & {
  connection: HubConnection;
  patch: (id: string, patch: Patch[]) => Promise<void>;
};

export const useProjectFilesWs = (
  project: Project,
  phaseIds: string[],
  estimateIds?: string[],
): UseProjectFilesWsHook => {
  const projectFiles = useProjectFiles();

  const [connected, setConnected] = React.useState(false);

  const connection = React.useRef<HubConnection>(
    new HubConnectionBuilder()
      .withUrl(`${process.env.CONTENT_API}/project/content?aid=${project.account?.id}&pid=${project.provider?.id}`, {
        accessTokenFactory: () => getAccessToken(aadConfig().contentScopes).then((r) => r as string),
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build(),
  );

  React.useEffect(() => {
    if (!projectFiles.category || !connected) return;

    // The file store is shared across phases. Clear it before requesting the
    // new scope so a previously-viewed phase's drawings never bleed into this
    // one while the fresh Fetch is in flight.
    projectFiles.setFiles([]);

    connection.current.send(
      'Fetch',
      project.id,
      phaseIds,
      estimateIds ?? [],
      Array.isArray(projectFiles.category) ? projectFiles.category : [projectFiles.category],
    );
  }, [projectFiles.category, project.id, phaseIds, estimateIds, connected]);

  // A 'Fetch' response is the authoritative snapshot for the requested
  // project/phase/category scope, so it REPLACES the store (scoped to this
  // project). The incremental events below only upsert.
  const [fetchContent, setFetchContent] = React.useState<ProjectContentEx[]>();
  React.useEffect(() => {
    if (!fetchContent) return;

    const ready = fetchContent.filter((x) => x.accountId === project.account?.id && x.projectId === project.id);
    projectFiles.setFiles(ready);
    setFetchContent(undefined);
  }, [fetchContent, projectFiles.setFiles, project.id, project.account?.id]);

  const [upsertContent, setUpsertContent] = React.useState<ProjectContentEx[]>();
  React.useEffect(() => {
    if (!upsertContent) return;

    const ready = upsertContent.filter(
      (x) =>
        x.accountId === project.account?.id &&
        x.projectId === project.id &&
        // Only content for the phase(s) currently in view. RecvNew / explode
        // events are broadcast to the whole account group, so a live upload on
        // another phase must not bleed into this phase's list. Exploded page
        // children may not carry a phaseId - let those through.
        (!x.phaseId || phaseIds.includes(x.phaseId)),
    );
    if (!ready.length) {
      setUpsertContent(undefined);
      return;
    }

    const newFiles = [...projectFiles.files];
    ready.forEach((c) => {
      const idx = newFiles.findIndex((x) => x.id === c.id);
      if (idx >= 0) newFiles[idx] = c;
      else newFiles.push(c);
    });

    projectFiles.setFiles(newFiles);
    setUpsertContent(undefined);
  }, [upsertContent, projectFiles.files, projectFiles.setFiles, project.id, project.account?.id, phaseIds]);

  const [deleteContent, setDeleteContent] = React.useState<ProjectContentEx>();
  React.useEffect(() => {
    if (!deleteContent) return;
    projectFiles.setFiles([...projectFiles.files].filter((x) => x.id !== deleteContent.id));
    setDeleteContent(undefined);
  }, [deleteContent, projectFiles.files, projectFiles.setFiles]);

  React.useEffect(() => {
    if (connection.current.state !== HubConnectionState.Disconnected) return;

    const handleDelete = (content: ProjectContentEx) => setDeleteContent(content);

    // cleanup, as needed
    connection.current.off('RecvFetch');
    connection.current.off('RecvNew');
    connection.current.off('RecvUpdate');
    connection.current.off('RecvDrawingExplodeStart');
    connection.current.off('RecvDrawingExplodePage');
    connection.current.off('RecvDrawingExplodeComplete');
    connection.current.off('RecvDelete');

    // Full snapshot -> replace. Everything else -> phase-scoped upsert.
    connection.current.on('RecvFetch', (content: ProjectContentEx[]) => setFetchContent(content));
    connection.current.on('RecvNew', (content: ProjectContentEx) => setUpsertContent([content]));
    connection.current.on('RecvUpdate', (content: ProjectContentEx) => setUpsertContent([content]));
    connection.current.on('RecvDrawingExplodeStart', (content: ProjectContentEx[]) => setUpsertContent(content));
    connection.current.on('RecvDrawingExplodePage', (content: ProjectContentEx[]) => setUpsertContent(content));
    connection.current.on('RecvDrawingExplodeComplete', (content: ProjectContentEx[]) => setUpsertContent(content));
    connection.current.on('RecvDelete', (content: ProjectContentEx) => handleDelete(content));

    connection.current.start().then(() => setConnected(true));

    return () => {
      if (connection.current.state === HubConnectionState.Connected) connection.current.stop();
    };
  }, []);

  // A patch is a person's decision about their own project, so it is never
  // dropped on the floor. Two rules, both learned the hard way:
  //
  //   1. WAIT for the socket instead of returning. This is reached by direct
  //      navigation to a single sheet, and by anything that happens while the
  //      connection is re-establishing itself. Returning early there discarded
  //      the save and resolved as though it had worked.
  //   2. INVOKE, not send. `send` is fire-and-forget: it does not wait for the
  //      server and never surfaces a server-side exception, so a failed write
  //      was indistinguishable from a successful one. `invoke` waits, and a
  //      rejection reaches the caller, who can tell the person.
  const patch = React.useCallback(
    async (id: string, patch: Patch[]) => {
      const c = connection.current;
      // Read through a function: the socket changes state underneath us, which
      // is the whole point of the wait below.
      const state = () => connection.current.state;

      if (state() !== HubConnectionState.Connected) {
        // Reconnecting resolves on its own; anything else has to be started.
        if (state() === HubConnectionState.Disconnected) await c.start();

        const deadline = Date.now() + PatchConnectTimeoutMs;
        while (state() !== HubConnectionState.Connected) {
          if (Date.now() > deadline) {
            throw new Error('Not connected to the content service, so the change was not saved.');
          }
          await new Promise((r) => setTimeout(r, 100));
        }
      }

      await c.invoke('Patch', project.id, id, patch);
    },
    [project.id],
  );

  return {
    ...projectFiles,
    connection: connection.current,
    patch,
  };
};
